import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  FlexRow,
  Grid,
  ISelectOption,
  NumberField,
  SelectField,
  SmallBodyText,
  TextField,
  useAsyncEffect,
} from 'ui-toolkit';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { depositCategoryOptions } from '../../../../../types/order/escrow/eDepositCategory';
import { IAchAccountResponse } from '../../../../../types/accounting/response/IEscrowExpectedAchPullDepositResponse';
import { accountTypeOptions } from '../../../../../types/order/escrow/IAchAccountData';
import { achPullPayerTypeOptions, eAchPullPayerType } from '../../../../../types/order/escrow/eAchPullPayerType';
import { useFormikContext } from 'formik';
import { IEscrowFileCompleteData } from '../../../../../types/order/escrow/IEscrowFileCompleteData';
import { getWireRoutingByRoutingNumber } from '../../../../../API/order/escrow/EscrowAPI';
import useDebounce from '../../../../../utilities/hooks/useDebounce';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { ePartyPurpose } from '../../../../../ui-toolkit/enums/ePartyPurpose';
import { IPartyPerson } from '../../../../../ui-toolkit/orders/interfaces/IPartyPerson';
import { isBuyerBorrower } from '../../../../../ui-toolkit/orders/interfaces/IParty';

export interface ICreateEditAchPullDepositRowProps {
  index: number;
  onDelete: (index: number) => void;
  onSend: (achPullId: string) => void;
  fieldName: string;
  achAccounts: IAchAccountResponse[];
  parties: IPartyPerson[];
  routingNumber?: string;
}

export const CreateEditAchPullDepositRow: React.FC<ICreateEditAchPullDepositRowProps> = ({
  index,
  onDelete,
  onSend,
  fieldName,
  achAccounts,
  parties,
  routingNumber,
}) => {
  const hasEditPermission = useHasPermissions([ePermissions.EscrowExpectedAchPullDepositsEdit]);

  const formik = useFormikContext<IEscrowFileCompleteData>();
  const isAchAccountPersisted: boolean =
    (formik.values.achPullDeposits && formik.values.achPullDeposits[index].account?.id !== undefined) || false;
  const achPullId = formik.values.achPullDeposits && formik.values.achPullDeposits[index].id;
  const isAchPullPersisted: boolean = achPullId !== undefined;
  const disableAchInput = isAchPullPersisted && isAchAccountPersisted;
  const [mewRoutingNumber, setNewRoutingNumber] = useState<string>(routingNumber || '');
  const debouncedRoutingNumber = useDebounce(mewRoutingNumber, 200);
  const [bankName, setBankName] = useState('');

  // parties
  const borrowerParties = parties.filter(isBuyerBorrower);
  const sellerParties = parties.filter((party) => {
    return party.purpose === ePartyPurpose.seller;
  });
  const sellerPartyOptions: ISelectOption[] = sellerParties.map((party) => ({
    label: [party.first_name, party.last_name].join(' '),
    value: party.id,
  }));
  const borrowerPartyOptions: ISelectOption[] = borrowerParties.map((party) => ({
    label: [party.first_name, party.last_name].join(' '),
    value: party.id,
  }));

  useAsyncEffect(async () => {
    if (debouncedRoutingNumber && debouncedRoutingNumber.length === 9) {
      try {
        const wireRouting = await getWireRoutingByRoutingNumber(debouncedRoutingNumber);
        setBankName(wireRouting ? wireRouting.name : '');
      } catch (e: any) {
        setBankName('');
      }
    } else {
      setBankName('');
    }
  }, [debouncedRoutingNumber]);

  return (
    <>
      <Grid>
        {/*Hidden - may be useful in the future, but don’t want ops to have access to it now*/}
        {/*{!disableAchInput && (*/}
        {/*  <Grid columnCount={1} alignItems={`center`} columnWidth={'50%'}>*/}
        {/*    <SelectField*/}
        {/*      name={`${fieldName}.${index}.account`}*/}
        {/*      options={getISelectOptionsFromIAchAccountResponse(achAccounts)}*/}
        {/*      label={'Existing ACH Account (Optional)'}*/}
        {/*      readOnly={!hasEditPermission}*/}
        {/*      isClearable*/}
        {/*      errorText={''}*/}
        {/*    />*/}
        {/*    <BoldBodyText>Or enter a new one</BoldBodyText>*/}
        {/*  </Grid>*/}
        {/*)}*/}
        <Grid templateColumns={'1fr 1fr 1fr 1fr 0.2fr'} alignItems={`center`}>
          <TextField
            name={`${fieldName}.${index}.account.name`}
            label="Name"
            placeholder="Samuel L. Jackson"
            readOnly={!hasEditPermission || disableAchInput}
          />
          <TextField
            name={`${fieldName}.${index}.account.routingNumber`}
            label="Routing Number"
            placeholder="999999999"
            readOnly={!hasEditPermission || disableAchInput}
            onChange={(value) => {
              setNewRoutingNumber(value || '');
            }}
            hintText={bankName}
          />
          <TextField
            name={`${fieldName}.${index}.account.accountNumber`}
            label="Account Number"
            placeholder="999999-99"
            readOnly={!hasEditPermission || disableAchInput}
          />
          <SelectField
            name={`${fieldName}.${index}.account.accountType`}
            options={accountTypeOptions}
            label={'Account Type'}
            isDisabled={!hasEditPermission || disableAchInput}
          />
          <FlexRow justifyContent={'start'} verticalAlign={'center'}>
            <Button
              intent={'ghost'}
              isInline
              type={'button'}
              onClick={() => onDelete(index)}
              disabled={!hasEditPermission}
            >
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </FlexRow>
        </Grid>
        <Grid templateColumns={'1fr 1fr 1fr 1fr 1fr 0.25fr'} alignItems={`center`}>
          <TextField
            name={`${fieldName}.${index}.wellsFargoTransactionNumber`}
            label="Memo"
            placeholder='StreetAddress-Zip (ex "123MainSt-90210")'
            readOnly={!hasEditPermission}
          />
          <NumberField
            name={`${fieldName}.${index}.amountCents`}
            label="Deposit Amount"
            mode="currency"
            placeholder="1,234.56"
            valueIsCents
            disabled={!hasEditPermission}
          />
          <SelectField
            name={`${fieldName}.${index}.category`}
            options={depositCategoryOptions}
            label={'Category'}
            isDisabled={!hasEditPermission}
          />
          <SelectField
            name={`${fieldName}.${index}.payerType`}
            options={achPullPayerTypeOptions}
            label={'Payer Type'}
            isDisabled={!hasEditPermission}
          />
          {/*TODO: load borrower OR seller options based on payer type*/}
          {formik.values.achPullDeposits &&
          formik.values.achPullDeposits[index].payerType === eAchPullPayerType.Borrower ? (
            <SelectField
              name={`${fieldName}.${index}.account.ownerId`}
              options={borrowerPartyOptions}
              label={'Borrower'}
              isDisabled={!hasEditPermission}
            />
          ) : (
            formik.values.achPullDeposits &&
            formik.values.achPullDeposits[index].payerType === eAchPullPayerType.Seller && (
              <SelectField
                name={`${fieldName}.${index}.account.ownerId`}
                options={sellerPartyOptions}
                label={'Seller'}
                isDisabled={!hasEditPermission}
              />
            )
          )}
        </Grid>
        {isAchPullPersisted && (
          <FlexRow justifyContent={'start'} verticalAlign={'center'}>
            <Button
              intent={'primary'}
              type={'button'}
              onClick={() => onSend(achPullId!)}
              disabled={!hasEditPermission || !isAchAccountPersisted || !formik.isValid}
            >
              Send
            </Button>
            {!isAchAccountPersisted && (
              <SmallBodyText intent={'italic'}>
                This ACH Deposit cannot be initiated until ACH Account info is provided within the Homeowner Portal.
              </SmallBodyText>
            )}
          </FlexRow>
        )}
      </Grid>
    </>
  );
};
